import { useRef, type ChangeEvent } from 'react';
import { PageHeader } from '@/components/shared/page-header';
import { LocationBadge } from '@/components/shared/location-badge';
import { ProductForm } from './product-form';
import { useProduct, useUpdateProduct, useRecordPriceChange } from '@/hooks/use-products';
import { useUploadProductImage, useDeleteProductImage } from '@/hooks/use-product-image';
import { usePermissions } from '@/hooks/use-permissions';
import { useLocationStore } from '@/stores/location-store';
import { useAuthStore } from '@/stores/auth-store';
import { useMatchedCommonProductImage } from '@/hooks/use-common-product-images';
import { useDepartments } from '@/hooks/use-departments';
import { toast } from '@/stores/toast-store';

const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg'];
const MAX_IMAGE_BYTES = 2 * 1024 * 1024; // 2 MB — matches the products service limit

/** Money off the API/form as a number, or null when absent.
 *  Prices arrive as strings ("12.00") and an emptied input arrives as '',
 *  which parseFloat would turn into NaN — and NaN !== NaN would record a
 *  bogus change on every save. */
function toPrice(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = parseFloat(String(value));
  return Number.isFinite(n) ? n : null;
}

interface Props {
  productId: string;
}

export function ProductEditPage({ productId }: Props) {
  // Pass the location switcher's selection to the GET so vendor links
  // (and any other per-location payload) match the location the user is
  // currently looking at. Without this, opening a product would show
  // every location's vendor rows mashed together.
  const { selectedLocationId } = useLocationStore();
  const { data, isLoading } = useProduct(productId, selectedLocationId || undefined);
  const updateProduct = useUpdateProduct();
  const recordPriceChange = useRecordPriceChange();
  const product = data?.data;

  // Generic product image preview — only when the super-admin enabled the
  // per-tenant switch. The products service resolves the match (department +
  // name + keyword) so this preview mirrors exactly what the POS register shows.
  const genericImagesEnabled = useAuthStore((s) => s.tenant?.useGenericProductImages === true);
  const { data: deptData } = useDepartments();
  const departmentName = deptData?.data?.find((d: any) => d.id === product?.departmentId)?.name ?? null;
  const { data: matchedImage } = useMatchedCommonProductImage(
    product?.name,
    departmentName,
    genericImagesEnabled && !!product?.name,
  );
  const genericImageUrl = genericImagesEnabled ? (matchedImage?.data?.imageUrl ?? null) : null;

  // Tenant product image upload — gated by the super-admin per-tenant switch.
  // The tenant's own image (product.imageUrl) takes priority over the generic
  // image, both in this preview and on POS.
  const canEditProducts = usePermissions().has('products.edit');
  const uploadEnabled = useAuthStore((s) => s.tenant?.allowTenantProductImageUpload === true) && canEditProducts;
  const uploadImage = useUploadProductImage();
  const deleteImage = useDeleteProductImage();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const ownImageUrl = product?.imageUrl || null;
  const displayImageUrl = ownImageUrl || genericImageUrl;

  const handleImageSelected = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so picking the same file again still fires onChange
    if (!file) return;
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      toast.error('Please select a PNG or JPG image');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error('Image must be 2 MB or smaller');
      return;
    }
    try {
      await uploadImage.mutateAsync({ productId, file });
      toast.success('Product image updated');
    } catch (err: any) {
      toast.error(err?.error?.message || 'Failed to upload image');
    }
  };

  const handleRemoveImage = async () => {
    try {
      await deleteImage.mutateAsync(productId);
      toast.success('Product image removed');
    } catch (err: any) {
      toast.error(err?.error?.message || 'Failed to remove image');
    }
  };

  const handleSubmit = async (formData: any) => {
    try {
      // Track price changes before updating
      const priceChanges: Array<{ priceType: string; oldValue: number | null; newValue: number | null }> = [];

      if (product) {
        // Selling price and cost are per-location: GET /products/:id returns
        // them inside `prices` (one entry per location) and has NO top-level
        // sellingPrice/cost. Reading those absent fields made every "previous"
        // value null, which is why Reports → Inventory → Price Updates showed
        // an empty Previous Price column — and why an unchanged price still
        // recorded a change (null !== 12 is always true).
        //
        // Resolve exactly the way ProductForm seeds its own inputs
        // (product-form.tsx `locationPrice`), so "previous" is by construction
        // the value the form started with and "new" is what the user left.
        const locationPrice = selectedLocationId
          ? product.prices?.find((p: any) => p.locationId === selectedLocationId)
          : null;
        const prevSelling = selectedLocationId
          ? locationPrice?.sellingPrice
          : (product.sellingPrice ?? product.cashPrice);
        const prevCost = selectedLocationId ? locationPrice?.cost : product.cost;

        const oldSelling = toPrice(prevSelling);
        const newSelling = toPrice(formData.sellingPrice);
        if (oldSelling !== newSelling) {
          priceChanges.push({ priceType: 'selling', oldValue: oldSelling, newValue: newSelling });
        }

        const oldCost = toPrice(prevCost);
        const newCost = toPrice(formData.cost);
        if (oldCost !== newCost) {
          priceChanges.push({ priceType: 'cost', oldValue: oldCost, newValue: newCost });
        }
      }

      await updateProduct.mutateAsync({ id: productId, ...formData });

      // Record price changes after successful update
      if (priceChanges.length > 0) {
        recordPriceChange.mutate({ productId, changes: priceChanges });
      }

      toast.success('Product updated successfully');
      window.location.hash = '#/pos-management';
    } catch (err: any) {
      // ProductForm catches these 409 codes and pins the message to the
      // offending input (barcode or SKU), so we rethrow without toasting.
      const code = err?.error?.code;
      if (code === 'DUPLICATE_PRIMARY_BARCODE' || code === 'DUPLICATE_SKU') {
        throw err;
      }
      const message = err?.error?.message || err?.error?.details?.[0]?.message;
      if (message && !message.includes('Internal server error')) {
        toast.error(message);
      }
    }
  };

  if (isLoading) {
    return <div className="flex h-64 items-center justify-center text-muted-foreground">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <PageHeader title={product?.name || 'Edit Product'} backTo="#/pos-management">
        <LocationBadge />
      </PageHeader>
      {(displayImageUrl || uploadEnabled) && (
        <div className="flex items-center gap-4 rounded-xl border border-border bg-white p-4">
          {displayImageUrl ? (
            <img
              src={displayImageUrl}
              alt={product?.name || 'Product image'}
              className="h-20 w-20 shrink-0 rounded-lg border border-border object-cover"
            />
          ) : (
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-lg border border-dashed border-border bg-muted/30 text-center text-[11px] text-muted-foreground">
              No image
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-foreground">Product image</div>
            <div className="text-xs text-muted-foreground">
              {ownImageUrl
                ? 'Your uploaded image — shown on POS.'
                : genericImageUrl
                  ? 'Generic image shown on POS, matched by keyword. Upload your own to override it.'
                  : 'Upload a product image (PNG or JPG, max 2 MB) to show on POS.'}
            </div>
            {uploadEnabled && (
              <div className="mt-2 flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  onChange={handleImageSelected}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadImage.isPending}
                  className="rounded-lg border border-border bg-white px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted/50 disabled:opacity-50"
                >
                  {uploadImage.isPending ? 'Uploading…' : ownImageUrl ? 'Replace image' : 'Upload image'}
                </button>
                {ownImageUrl && (
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    disabled={deleteImage.isPending}
                    className="rounded-lg border border-border bg-white px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    {deleteImage.isPending ? 'Removing…' : 'Remove'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Force a remount when the active location changes so ProductForm's
          useState initializers re-run against the freshly-fetched
          per-location data. Without this, switching the header location
          dropdown re-fetches the GET but the form keeps showing the old
          location's units / UOM / pricing because the state is sticky. */}
      <ProductForm
        key={`${productId}::${selectedLocationId ?? 'all'}`}
        initialData={product}
        onSubmit={handleSubmit}
        isSubmitting={updateProduct.isPending}
      />
    </div>
  );
}
